import { useI18nContext } from '@/core/providers/I18nProvider';
import {
  getAmenityCatalog,
  getAmenityCatalogRevision,
  subscribeAmenityCatalog,
  type AmenityCatalogEntry,
} from '@/core/services/amenityCatalogService';
import { normalizeAmenityName } from '@/modules/property-details/services/amenityService';
import { useEffect, useMemo, useState } from 'react';
import { useWebAmenities, type WebAmenity } from './useWebAmenities';

/**
 * Mobile-only wrapper around `useWebAmenities`. Native property sources (e.g.
 * the email-login profile-details `units[].amenities`) don't always share
 * Contentful's exact `value` casing/spacing the way ADHA's source does, so
 * exact-match misses here get a second, normalized (case/whitespace/
 * separator-insensitive) pass against the same catalog before falling back
 * to the default icon. Kept out of `useWebAmenities` so web's matching stays
 * untouched — this file has no web consumers.
 */
export function useMobileAmenities(
  amenities: readonly string[] | null | undefined,
): WebAmenity[] {
  const { currentLanguage } = useI18nContext();
  const exactMatched = useWebAmenities(amenities);

  const [catalogRevision, setCatalogRevision] = useState(
    getAmenityCatalogRevision,
  );
  useEffect(() => {
    return subscribeAmenityCatalog(() => {
      setCatalogRevision(getAmenityCatalogRevision());
    });
  }, []);

  return useMemo(() => {
    const hasUnmatched = exactMatched.some((item) => item.iconUrl === null);
    if (!hasUnmatched) {
      return exactMatched;
    }

    const catalog = getAmenityCatalog(currentLanguage);
    const normalizedCatalog = new Map<string, AmenityCatalogEntry>();
    for (const entry of catalog.values()) {
      const key = normalizeAmenityName(entry.value);
      if (!normalizedCatalog.has(key)) {
        normalizedCatalog.set(key, entry);
      }
    }

    return exactMatched.map((item) => {
      if (item.iconUrl !== null) {
        return item;
      }
      const fallback = normalizedCatalog.get(normalizeAmenityName(item.value));
      if (!fallback) {
        return item;
      }
      return { value: item.value, label: fallback.label, iconUrl: fallback.iconUrl };
    });
    // catalogRevision re-resolves the fallback once the central preload lands.
  }, [exactMatched, currentLanguage, catalogRevision]);
}
